<template>
  <div class="sale-document-print-wrapper">
    <article class="sale-document-print">
      <header class="sale-document-header">
        <div class="sale-document-brand">
          <img
            v-if="shopLogo"
            :src="shopLogo"
            alt="Logo"
            class="sale-document-logo"
            @error="hideLogo"
          />
          <div v-else class="sale-document-logo-fallback">
            {{ logoText }}
          </div>
          <div>
            <h2>{{ businessName }}</h2>
            <p v-if="shopLegalName">{{ shopLegalName }}</p>
            <p>
              <span v-if="shopAddress">{{ shopAddress }}</span>
              <span v-if="shopPhone"><br />{{ shopPhone }}</span>
              <span v-if="shopEmail"><br />{{ shopEmail }}</span>
              <span v-if="shopTaxId"><br />NIF : {{ shopTaxId }}</span>
            </p>
          </div>
        </div>

        <div class="sale-document-title">
          <span>{{ documentTitle }}</span>
          <strong>{{ documentNumber }}</strong>
          <small>Date : {{ formatDocumentDate(documentIssuedAt || sale.created_at) }}</small>
        </div>
      </header>

      <section class="sale-document-parties">
        <div>
          <span>Client</span>
          <strong>{{ clientName }}</strong>
          <p>
            <span v-if="clientPhone">{{ clientPhone }}</span>
            <span v-if="clientEmail"><br />{{ clientEmail }}</span>
            <span v-if="clientAddress"><br />{{ clientAddress }}</span>
          </p>
        </div>
        <div>
          <span>Vente</span>
          <strong>{{ sale.reference }}</strong>
          <p>
            Créée le {{ formatDocumentDate(sale.created_at) }}
            <span v-if="sale.payment_method"><br />Paiement : {{ paymentMethodLabel(sale.payment_method) }}</span>
            <span v-if="sale.payment_reference"><br />Référence : {{ sale.payment_reference }}</span>
          </p>
        </div>
      </section>

      <table class="sale-document-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Produit</th>
            <th class="text-end">Qté</th>
            <th class="text-end">PU</th>
            <th class="text-end">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in activeItems" :key="item.id || index">
            <td>{{ index + 1 }}</td>
            <td>
              <strong>{{ item.product_name || item.product?.name || 'Produit' }}</strong>
              <small v-if="lineSku(item)">Réf. {{ lineSku(item) }}</small>
            </td>
            <td class="text-end">{{ remainingQuantity(item) }}</td>
            <td class="text-end">{{ $format.currency(item.unit_price || 0) }}</td>
            <td class="text-end">{{ $format.currency(activeLineTotal(item)) }}</td>
          </tr>
        </tbody>
      </table>

      <section class="sale-document-bottom">
        <div class="sale-document-qr">
          <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="QR code du document" />
          <div v-else class="sale-document-qr-placeholder">QR</div>
          <span>Code de vérification</span>
        </div>

        <div class="sale-document-totals">
          <div>
            <span>Sous-total</span>
            <strong>{{ $format.currency(sale.subtotal_amount || sale.total_amount || 0) }}</strong>
          </div>
          <div v-if="Number(sale.discount_amount || 0) > 0">
            <span>Remise</span>
            <strong>- {{ $format.currency(sale.discount_amount || 0) }}</strong>
          </div>
          <div v-if="Number(sale.tva_amount || 0) > 0">
            <span>TVA incluse</span>
            <strong>{{ $format.currency(sale.tva_amount || 0) }}</strong>
          </div>
          <div v-if="Number(sale.css_amount || 0) > 0">
            <span>CSS incluse</span>
            <strong>{{ $format.currency(sale.css_amount || 0) }}</strong>
          </div>
          <div class="sale-document-total-net">
            <span>Total net</span>
            <strong>{{ $format.currency(sale.total_amount || 0) }}</strong>
          </div>
        </div>
      </section>

      <footer class="sale-document-footer">
        <span>{{ documentTitle }} généré par {{ businessName }}</span>
        <small>powered by Suite241</small>
      </footer>
    </article>
  </div>
</template>

<script>
export default {
  name: 'SaleDocumentPrint',
  props: {
    sale: {
      type: Object,
      required: true,
    },
    shopInfo: {
      type: Object,
      default: () => ({}),
    },
    document: {
      type: Object,
      required: true,
    },
    qrCodeUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      logoFailed: false,
    };
  },
  computed: {
    activeItems() {
      return (this.sale.items || []).filter((item) => this.remainingQuantity(item) > 0);
    },
    documentTitle() {
      return this.document.title || 'DOCUMENT';
    },
    documentNumber() {
      return this.document.number || this.sale.reference;
    },
    documentIssuedAt() {
      return this.document.issued_at || '';
    },
    businessName() {
      return this.shopInfo.name || this.shopInfo.business_name || 'Suite241 Gestion Commerciale';
    },
    shopLegalName() {
      return this.shopInfo.legal_name || this.shopInfo.company_name || '';
    },
    shopAddress() {
      return [this.shopInfo.address, this.shopInfo.city, this.shopInfo.country].filter(Boolean).join(', ');
    },
    shopPhone() {
      return this.shopInfo.phone || this.shopInfo.whatsapp_phone || '';
    },
    shopEmail() {
      return this.shopInfo.email || '';
    },
    shopTaxId() {
      return this.shopInfo.tax_id || this.shopInfo.nif || '';
    },
    shopLogo() {
      if (this.logoFailed) {
        return '';
      }

      return this.shopInfo.main_logo_url || this.shopInfo.logo_url || this.shopInfo.logo || '';
    },
    logoText() {
      return this.businessName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('') || 'S';
    },
    clientName() {
      return this.sale.client?.name || this.sale.customer_name || 'Client comptant';
    },
    clientPhone() {
      return this.sale.client?.phone || '';
    },
    clientEmail() {
      return this.sale.client?.email || '';
    },
    clientAddress() {
      const client = this.sale.client || {};

      return [client.address, client.city].filter(Boolean).join(', ');
    },
  },
  methods: {
    hideLogo() {
      this.logoFailed = true;
    },
    remainingQuantity(item) {
      return Math.max(Number(item.quantity || 0) - Number(item.cancelled_quantity || 0), 0);
    },
    activeLineTotal(item) {
      return this.remainingQuantity(item) * Number(item.unit_price || 0);
    },
    lineSku(item) {
      return item.product?.sku || item.product_sku || '';
    },
    formatDocumentDate(value) {
      if (!value) {
        return '—';
      }

      if (this.$format?.datetime) {
        return this.$format.datetime(value);
      }

      return this.$format?.date ? this.$format.date(value) : value;
    },
    paymentMethodLabel(method) {
      switch (method) {
        case 'cash':
          return 'Espèces';
        case 'credit':
          return 'Crédit';
        case 'mobile_money':
          return 'Mobile money';
        case 'card':
          return 'Carte bancaire';
        default:
          return '—';
      }
    },
  },
};
</script>

<style scoped>
.sale-document-print-wrapper {
  background: #f3f4f6;
  padding: 1rem;
}

.sale-document-print {
  background: #ffffff;
  color: #111827;
  font-size: 0.92rem;
  margin: 0 auto;
  max-width: 210mm;
  min-height: 297mm;
  padding: 16mm;
}

.sale-document-header {
  align-items: flex-start;
  border-bottom: 2px solid #111827;
  display: flex;
  gap: 1.5rem;
  justify-content: space-between;
  padding-bottom: 1rem;
}

.sale-document-brand {
  align-items: flex-start;
  display: flex;
  gap: 0.9rem;
  min-width: 0;
}

.sale-document-logo,
.sale-document-logo-fallback {
  align-items: center;
  border: 1px solid #d1d5db;
  display: flex;
  flex: 0 0 64px;
  height: 64px;
  justify-content: center;
  object-fit: contain;
  width: 64px;
}

.sale-document-logo-fallback {
  background: #111827;
  color: #ffffff;
  font-weight: 800;
}

.sale-document-brand h2 {
  font-size: 1.2rem;
  font-weight: 800;
  margin: 0 0 0.2rem;
}

.sale-document-brand p,
.sale-document-title small,
.sale-document-parties p {
  color: #374151;
  margin: 0;
}

.sale-document-title {
  min-width: 190px;
  text-align: right;
}

.sale-document-title span {
  display: block;
  font-size: 1.55rem;
  font-weight: 900;
  letter-spacing: 0.08em;
}

.sale-document-title strong {
  display: block;
  font-size: 0.95rem;
  margin: 0.25rem 0;
}

.sale-document-parties {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 1.3rem 0;
}

.sale-document-parties > div {
  border: 1px solid #d1d5db;
  padding: 0.85rem;
}

.sale-document-parties span,
.sale-document-qr span {
  color: #6b7280;
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.sale-document-parties strong {
  display: block;
  font-size: 1rem;
  margin: 0.15rem 0 0.35rem;
}

.sale-document-table {
  border-collapse: collapse;
  width: 100%;
}

.sale-document-table th,
.sale-document-table td {
  border-bottom: 1px solid #d1d5db;
  padding: 0.65rem 0.45rem;
  vertical-align: top;
}

.sale-document-table th {
  background: #111827;
  color: #ffffff;
  font-size: 0.78rem;
  text-transform: uppercase;
}

.sale-document-table td small {
  color: #6b7280;
  display: block;
  margin-top: 0.15rem;
}

.sale-document-bottom {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 1.2rem;
}

.sale-document-qr {
  text-align: center;
}

.sale-document-qr img,
.sale-document-qr-placeholder {
  border: 1px solid #d1d5db;
  height: 116px;
  margin-bottom: 0.35rem;
  width: 116px;
}

.sale-document-qr-placeholder {
  align-items: center;
  display: flex;
  font-weight: 900;
  justify-content: center;
}

.sale-document-totals {
  margin-left: auto;
  min-width: 270px;
}

.sale-document-totals > div {
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  padding: 0.45rem 0;
}

.sale-document-total-net {
  border-bottom: 2px solid #111827 !important;
  border-top: 2px solid #111827;
  font-size: 1.05rem;
}

.sale-document-footer {
  border-top: 1px solid #d1d5db;
  color: #4b5563;
  margin-top: 2rem;
  padding-top: 0.7rem;
  text-align: center;
}

.sale-document-footer small {
  display: block;
  font-size: 8px;
  margin-top: 5px;
}

@media print {
  .sale-document-print-wrapper {
    background: #ffffff;
    padding: 0;
  }

  .sale-document-print {
    box-shadow: none;
    margin: 0;
    min-height: auto;
    padding: 0;
  }
}
</style>
